import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CULTURE_TOPICS } from "@/data/culture";
import {
  TIMELINE,
  FIGURES,
  SYMBOLS,
  KREYOL_PHRASES,
  PROVERBS,
} from "@/data/konesans";
import {
  BookOpen,
  Search,
  Clock,
  Users,
  Flag,
  Languages,
  Quote,
  ArrowUpRight,
} from "lucide-react";

const TABS = [
  { id: "topics", label: "Topics", icon: BookOpen, testid: "kon-tab-topics" },
  { id: "timeline", label: "Timeline", icon: Clock, testid: "kon-tab-timeline" },
  { id: "figures", label: "Figures", icon: Users, testid: "kon-tab-figures" },
  { id: "symbols", label: "Symbols", icon: Flag, testid: "kon-tab-symbols" },
  { id: "kreyol", label: "Kreyòl", icon: Languages, testid: "kon-tab-kreyol" },
  { id: "proverbs", label: "Proverbs", icon: Quote, testid: "kon-tab-proverbs" },
];

export default function Konesans() {
  const [tab, setTab] = useState("topics");
  const [q, setQ] = useState("");

  const query = q.trim().toLowerCase();

  const filteredTopics = useMemo(() => {
    if (!query) return CULTURE_TOPICS;
    return CULTURE_TOPICS.filter(
      (t) =>
        t.title.toLowerCase().includes(query) ||
        t.titleKreyol.toLowerCase().includes(query) ||
        t.blurb.toLowerCase().includes(query) ||
        t.intro.toLowerCase().includes(query)
    );
  }, [query]);

  const filteredTimeline = useMemo(
    () => (query ? TIMELINE.filter((t) => t.event.toLowerCase().includes(query) || t.year.includes(query)) : TIMELINE),
    [query]
  );
  const filteredFigures = useMemo(
    () =>
      query
        ? FIGURES.filter(
            (f) =>
              f.name.toLowerCase().includes(query) ||
              f.role.toLowerCase().includes(query) ||
              f.note.toLowerCase().includes(query)
          )
        : FIGURES,
    [query]
  );
  const filteredSymbols = useMemo(
    () => (query ? SYMBOLS.filter((s) => s.title.toLowerCase().includes(query) || s.body.toLowerCase().includes(query)) : SYMBOLS),
    [query]
  );
  const filteredKreyol = useMemo(
    () => (query ? KREYOL_PHRASES.filter((p) => p.kr.toLowerCase().includes(query) || p.en.toLowerCase().includes(query)) : KREYOL_PHRASES),
    [query]
  );
  const filteredProverbs = useMemo(
    () => (query ? PROVERBS.filter((p) => p.kr.toLowerCase().includes(query) || p.en.toLowerCase().includes(query)) : PROVERBS),
    [query]
  );

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">── Konesans</div>
        <div className="flex h-3 w-10 overflow-hidden">
          <div className="flex-1 bg-[#00209F]" />
          <div className="flex-1 bg-[#D21034]" />
        </div>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-tighter">
        The Haitian <span className="text-[#ff4500]">Encyclopedia</span>
      </h1>
      <p className="text-[#a3a3a3] mt-4 text-sm max-w-2xl leading-relaxed">
        History, language, symbols, proverbs and the figures who built Ayiti — a
        living reference for the diaspora and the world.
      </p>

      {/* Search */}
      <div className="mt-8 relative max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search history, figures, proverbs, Kreyòl…"
          data-testid="kon-search"
          className="w-full bg-[#141414] border border-[#2a2a2a] focus:border-[#ff4500] outline-none pl-10 pr-4 py-2.5 text-sm"
        />
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-2 overflow-x-auto pb-2" data-testid="kon-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
              className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.2em] border whitespace-nowrap transition-colors ${
                active
                  ? "bg-[#1a1a1a] border-[#ff4500] text-white"
                  : "border-[#2a2a2a] text-[#a3a3a3] hover:text-white hover:border-[#3a3a3a]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {tab === "topics" && <TopicsGrid topics={filteredTopics} />}
        {tab === "timeline" && <Timeline items={filteredTimeline} />}
        {tab === "figures" && <FiguresGrid items={filteredFigures} />}
        {tab === "symbols" && <SymbolsGrid items={filteredSymbols} />}
        {tab === "kreyol" && <PhraseTable items={filteredKreyol} left="Kreyòl" right="English" testid="kreyol" />}
        {tab === "proverbs" && <PhraseTable items={filteredProverbs} left="Kreyòl proverb" right="Meaning" testid="proverbs" />}
      </div>
    </div>
  );
}

function TopicsGrid({ topics }) {
  if (topics.length === 0) return <EmptyState />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {topics.map((t) => (
        <Link
          key={t.slug}
          to={`/app/explore/${t.slug}`}
          data-testid={`kon-topic-${t.slug}`}
          className="group relative bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500]/60 transition-colors overflow-hidden"
        >
          <div className="aspect-[4/3] bg-black overflow-hidden">
            <img
              src={t.image}
              alt={t.title}
              className="w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
            />
          </div>
          <div className="p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] font-mono" style={{ color: t.accent }}>
              {t.titleKreyol}
            </div>
            <div className="flex items-start justify-between mt-2">
              <h3 className="font-display text-xl tracking-tight">{t.title}</h3>
              <ArrowUpRight className="w-4 h-4 text-[#a3a3a3] group-hover:text-[#ff4500] transition-colors" />
            </div>
            <p className="text-xs text-[#a3a3a3] mt-2 leading-relaxed">{t.blurb}</p>
          </div>
        </Link>
      ))}
    </div>
  );
}

function Timeline({ items }) {
  if (items.length === 0) return <EmptyState />;
  return (
    <div className="relative border-l-2 border-[#2a2a2a] ml-3 space-y-6">
      {items.map((t, i) => (
        <div key={i} className="relative pl-8" data-testid={`kon-timeline-${i}`}>
          <div className="absolute -left-[9px] top-1.5 w-4 h-4 bg-[#ff4500] border-2 border-[#0a0a0a] rounded-sm" />
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#ff4500]">{t.year}</div>
          <div className="text-sm text-[#f5f5f5] mt-1 leading-relaxed">{t.event}</div>
        </div>
      ))}
    </div>
  );
}

function FiguresGrid({ items }) {
  if (items.length === 0) return <EmptyState />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((f, i) => (
        <div key={i} className="bg-[#141414] border border-[#2a2a2a] p-5 hover:border-[#ff4500]/50 transition-colors" data-testid={`kon-figure-${i}`}>
          <div className="font-display text-lg tracking-tight">{f.name}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500] font-mono mt-1">{f.role}</div>
          <p className="text-xs text-[#a3a3a3] mt-3 leading-relaxed">{f.note}</p>
        </div>
      ))}
    </div>
  );
}

function SymbolsGrid({ items }) {
  if (items.length === 0) return <EmptyState />;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {items.map((s, i) => (
        <div key={i} className="bg-[#141414] border border-[#2a2a2a] p-5" data-testid={`kon-symbol-${i}`}>
          <div className="flex items-center gap-2">
            <div className="flex h-3 w-8 overflow-hidden">
              <div className="flex-1 bg-[#00209F]" />
              <div className="flex-1 bg-[#D21034]" />
            </div>
            <div className="font-display text-lg tracking-tight">{s.title}</div>
          </div>
          <p className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">{s.body}</p>
        </div>
      ))}
    </div>
  );
}

function PhraseTable({ items, left, right, testid }) {
  if (items.length === 0) return <EmptyState />;
  return (
    <div className="border border-[#2a2a2a] overflow-hidden">
      <div className="grid grid-cols-2 bg-[#141414] border-b border-[#2a2a2a] px-4 py-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff4500] font-mono">{left}</div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff4500] font-mono">{right}</div>
      </div>
      {items.map((p, i) => (
        <div
          key={i}
          data-testid={`kon-${testid}-${i}`}
          className="grid grid-cols-2 px-4 py-3 border-b border-[#2a2a2a] last:border-b-0 hover:bg-[#141414] transition-colors"
        >
          <div className="text-sm font-display">{p.kr}</div>
          <div className="text-sm text-[#a3a3a3]">{p.en}</div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-[#2a2a2a] p-16 text-center text-sm text-[#a3a3a3]">
      No matches. Try a different word.
    </div>
  );
}
